import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useUser } from '../contexts/UserContext';
import { Crown, Star, Edit3, Building2 } from 'lucide-react';

const TierIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.3s ease;

  &.free {
    background: rgba(255, 255, 255, 0.1);
    color: #cccccc;
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.15);
      transform: translateY(-1px);
    }
  }

  &.premium {
    background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
    color: #ffffff;
    border: 1px solid #ff6b35;
    box-shadow: 0 4px 15px rgba(255, 107, 53, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(255, 107, 53, 0.4);
    }
  }
`;

const TierIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TierText = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.2;
`;

const TierLabel = styled.span`
  font-size: 0.75rem;
  opacity: 0.8;
`;

const TierName = styled.span`
  font-size: 0.875rem;
  font-weight: 700;
`;

const UserTierIndicator = ({ onClick }) => {
  const { user, isPremium, isAdmin, isAuthor, isBrand } = useUser();
  const navigate = useNavigate();

  if (!user) return null;

  const handleClick = (e) => {
    // Prevent event bubbling that might interfere with mobile menu
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Navigate first
    if (isAdmin()) {
      // Admin users go to admin dashboard
      navigate('/admin');
    } else {
      // All other users (including Brand users) go to user dashboard
      navigate('/user-dashboard');
    }
    
    // Call the passed onClick callback after navigation (for menu closing, etc.)
    if (onClick) {
      // Use a small delay to ensure navigation happens first
      setTimeout(() => {
        onClick();
      }, 50);
    }
  };

  const isAdminUser = isAdmin();
  const isAuthorUser = isAuthor();
  const isBrandUser = isBrand();
  const isPremiumUser = isPremium();
  
  let tierClass, tierLabel, tierName, Icon;
  if (isAdminUser) {
    tierClass = 'premium'; // Use premium styling for admin
    tierLabel = '';
    tierName = 'Admin';
    Icon = Crown;
  } else if (isAuthorUser) {
    tierClass = 'premium'; // Use premium styling for author
    tierLabel = '';
    tierName = 'Author';
    Icon = Edit3;
  } else if (isBrandUser) {
    tierClass = 'premium'; // Use premium styling for brand
    tierLabel = '';
    tierName = 'Brand';
    Icon = Building2;
  } else if (isPremiumUser) {
    tierClass = 'premium';
    tierLabel = '';
    tierName = 'Premium';
    Icon = Crown;
  } else {
    tierClass = 'free';
    tierLabel = '';
    tierName = 'User';
    Icon = Star;
  }

  return (
    <TierIndicator 
      className={tierClass}
      onClick={handleClick}
      onTouchStart={(e) => {
        // Prevent default touch behavior
        e.stopPropagation();
      }}
      onTouchEnd={(e) => {
        // Ensure touch events work on mobile
        e.preventDefault();
        e.stopPropagation();
        handleClick(e);
      }}
      style={{ 
        cursor: 'pointer', 
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation', // Improves touch responsiveness
        userSelect: 'none', // Prevent text selection on mobile
        WebkitUserSelect: 'none'
      }}
    >
      <TierIcon>
        <Icon size={16} />
      </TierIcon>
      <TierText>
        {tierLabel && <TierLabel>{tierLabel}</TierLabel>}
        <TierName>{tierName}</TierName>
      </TierText>
    </TierIndicator>
  );
};

export default UserTierIndicator;
